export const RESERVATION_STATUSES = {
  HELD: 'held',
  PICKED_UP: 'picked_up',
  RETURNED: 'returned',
};

export const QR_VERIFICATION_STATUSES = {
  VALID: 'valid',
  PICKED_UP: 'picked_up',
  RETURNED: 'returned',
  INVALID: 'invalid',
};

/**
 * Contaminant model:
 * {
 *   id: string,
 *   name: string,
 *   healthFocus: string,
 *   summary: string,
 * }
 */
export const CONTAMINANTS = [
  {
    id: 'radon',
    name: 'Radon',
    healthFocus: 'Lung cancer risk',
    summary: 'A naturally occurring radioactive gas that can build up in lower levels of a home.',
  },
  {
    id: 'carbon_monoxide',
    name: 'Carbon monoxide',
    healthFocus: 'Acute poisoning and respiratory danger',
    summary: 'An odorless gas from fuel-burning appliances that can quickly become dangerous indoors.',
  },
  {
    id: 'indoor_air_pollution',
    name: 'Indoor air pollution',
    healthFocus: 'Asthma, irritation, and long-term respiratory symptoms',
    summary: 'Poor ventilation and indoor pollutants can contribute to headaches, irritation, and breathing issues.',
  },
  {
    id: 'voc_formaldehyde',
    name: 'VOC and formaldehyde',
    healthFocus: 'Respiratory irritation and potential cancer risk',
    summary: 'Volatile organic compounds can be released by building materials, furnishings, and solvents.',
  },
  {
    id: 'lead',
    name: 'Lead',
    healthFocus: 'Neurologic and developmental harm',
    summary: 'Lead can remain in paint, dust, soil, plumbing, and older fixtures in homes and schools.',
  },
  {
    id: 'water_contaminants',
    name: 'Water contaminants',
    healthFocus: 'Exposure to metals, nitrates, and other contaminants',
    summary: 'Private wells and aging systems may need routine screening for common water hazards.',
  },
  {
    id: 'mold_moisture',
    name: 'Mold and moisture',
    healthFocus: 'Respiratory irritation and asthma flare-ups',
    summary: 'Hidden moisture can support mold growth and worsen breathing concerns indoors.',
  },
];

/**
 * Equipment model:
 * {
 *   id: string,
 *   name: string,
 *   contaminantId: string,
 *   detects: string,
 *   healthImportance: string,
 *   recommendedTestingLocation: string,
 *   safetyNote: string,
 *   searchTerms: string[],
 * }
 */
export const EQUIPMENT_CATALOG = [
  {
    id: 'radon_detector',
    name: 'Radon detector',
    contaminantId: 'radon',
    detects: 'Longer-term indoor radon levels in basements and lower living areas.',
    healthImportance: 'Radon exposure is a leading environmental cause of lung cancer, especially with long-term exposure.',
    recommendedTestingLocation: 'Lowest lived-in level of the home, away from drafts, kitchens, and bathrooms.',
    safetyNote: 'Leave the detector undisturbed during the sampling period and follow the device instructions exactly.',
    searchTerms: ['radon', 'basement', 'lung', 'gas'],
  },
  {
    id: 'carbon_monoxide_detector',
    name: 'Carbon monoxide detector',
    contaminantId: 'carbon_monoxide',
    detects: 'Carbon monoxide from furnaces, gas stoves, fireplaces, generators, or attached garages.',
    healthImportance: 'Carbon monoxide can cause headaches, dizziness, loss of consciousness, and life-threatening poisoning.',
    recommendedTestingLocation: 'Near sleeping areas and on floors where fuel-burning appliances are present.',
    safetyNote: 'Leave the area and call emergency services if the monitor alarms or anyone feels suddenly ill.',
    searchTerms: ['co', 'carbon monoxide', 'furnace', 'alarm'],
  },
  {
    id: 'indoor_air_quality_monitor',
    name: 'Indoor air quality monitor',
    contaminantId: 'indoor_air_pollution',
    detects: 'Particles, humidity, temperature, and indoor air quality indicators that can reflect ventilation issues.',
    healthImportance: 'Poor indoor air can aggravate asthma, trigger respiratory symptoms, and contribute to daily exposures.',
    recommendedTestingLocation: 'Main living spaces, bedrooms, or work areas where people spend the most time.',
    safetyNote: 'Use trends over time rather than a single reading when deciding whether follow-up testing is needed.',
    searchTerms: ['air quality', 'pm', 'humidity', 'ventilation'],
  },
  {
    id: 'voc_formaldehyde_detector',
    name: 'VOC/formaldehyde detector',
    contaminantId: 'voc_formaldehyde',
    detects: 'Volatile organic compounds and formaldehyde from paints, flooring, cabinets, and other household products.',
    healthImportance: 'Elevated VOCs may contribute to eye, nose, and throat irritation and some compounds are linked to cancer risk.',
    recommendedTestingLocation: 'Recently renovated rooms, storage spaces, or areas with strong chemical odors.',
    safetyNote: 'Increase ventilation if readings are elevated and avoid using this tool as the sole basis for remediation decisions.',
    searchTerms: ['voc', 'formaldehyde', 'renovation', 'odors'],
  },
  {
    id: 'lead_paint_water_testing_kit',
    name: 'Lead paint/water testing kit',
    contaminantId: 'lead',
    detects: 'Potential lead in old paint chips, dust, or drinking water from older plumbing systems.',
    healthImportance: 'Lead exposure can be especially harmful for children and pregnant people and may affect neurologic health.',
    recommendedTestingLocation: 'Homes built before the late 1970s, older window frames, and taps with older service lines.',
    safetyNote: 'Use gloves, avoid creating dust, and contact a certified professional if you suspect extensive lead hazards.',
    searchTerms: ['lead', 'paint', 'water', 'dust', 'pipes'],
  },
  {
    id: 'well_water_contaminant_testing_kit',
    name: 'Well water contaminant testing kit',
    contaminantId: 'water_contaminants',
    detects: 'Common private well concerns such as nitrates, bacteria indicators, or selected metals depending on the kit.',
    healthImportance: 'Contaminated well water can contribute to gastrointestinal illness and long-term exposure concerns.',
    recommendedTestingLocation: 'Private wells, seasonal properties, and taps used for drinking or cooking.',
    safetyNote: 'Collect samples using the included instructions to reduce contamination and false results.',
    searchTerms: ['well', 'water', 'nitrate', 'drinking water'],
  },
  {
    id: 'mold_moisture_assessment_tools',
    name: 'Mold/moisture assessment tools',
    contaminantId: 'mold_moisture',
    detects: 'Moisture intrusion, damp materials, and hidden conditions that can support mold growth.',
    healthImportance: 'Moisture and mold can worsen asthma, cause irritation, and signal building conditions that need attention.',
    recommendedTestingLocation: 'Bathrooms, basements, window frames, utility rooms, and areas with visible staining or odor.',
    safetyNote: 'Do not disturb large mold growth without protective equipment and professional advice when warranted.',
    searchTerms: ['mold', 'moisture', 'dampness', 'humidity'],
  },
];

/**
 * Pickup location model:
 * {
 *   id: string,
 *   name: string,
 *   address: string,
 *   openingHours: string[],
 * }
 */
export const PICKUP_LOCATIONS = [
  {
    id: 'kingston_central_library',
    name: 'Kingston Central Library',
    address: '130 Johnson St, Kingston, ON K7L 1X8',
    openingHours: [
      'Mon-Thu: 9:00 AM - 8:00 PM',
      'Fri-Sat: 9:00 AM - 5:00 PM',
      'Sun: 1:00 PM - 5:00 PM',
    ],
  },
  {
    id: 'queens_university_library',
    name: "Queen's University Library",
    address: '77 Union St, Kingston, ON K7L 2N9',
    openingHours: [
      'Mon-Fri: 8:00 AM - 10:00 PM',
      'Sat-Sun: 10:00 AM - 6:00 PM',
    ],
  },
  {
    id: 'community_health_centre',
    name: 'Community Health Centre',
    address: '263 Weller Ave, Kingston, ON K7K 2V4',
    openingHours: [
      'Mon-Thu: 8:30 AM - 6:00 PM',
      'Fri: 8:30 AM - 4:30 PM',
      'Sat-Sun: Closed',
    ],
  },
];

/**
 * Inventory model:
 * {
 *   id: string,
 *   locationId: string,
 *   equipmentId: string,
 *   totalCount: number,
 *   availableCount: number,
 *   onLoanCount: number,
 * }
 */
export const INITIAL_INVENTORY = [
  {
    id: 'inv-kingston-radon',
    locationId: 'kingston_central_library',
    equipmentId: 'radon_detector',
    totalCount: 4,
    availableCount: 3,
    onLoanCount: 1,
  },
  {
    id: 'inv-kingston-co',
    locationId: 'kingston_central_library',
    equipmentId: 'carbon_monoxide_detector',
    totalCount: 2,
    availableCount: 2,
    onLoanCount: 0,
  },
  {
    id: 'inv-kingston-iaq',
    locationId: 'kingston_central_library',
    equipmentId: 'indoor_air_quality_monitor',
    totalCount: 2,
    availableCount: 1,
    onLoanCount: 1,
  },
  {
    id: 'inv-queens-radon',
    locationId: 'queens_university_library',
    equipmentId: 'radon_detector',
    totalCount: 2,
    availableCount: 2,
    onLoanCount: 0,
  },
  {
    id: 'inv-queens-voc',
    locationId: 'queens_university_library',
    equipmentId: 'voc_formaldehyde_detector',
    totalCount: 2,
    availableCount: 1,
    onLoanCount: 1,
  },
  {
    id: 'inv-chc-lead',
    locationId: 'community_health_centre',
    equipmentId: 'lead_paint_water_testing_kit',
    totalCount: 2,
    availableCount: 1,
    onLoanCount: 1,
  },
  {
    id: 'inv-chc-water',
    locationId: 'community_health_centre',
    equipmentId: 'well_water_contaminant_testing_kit',
    totalCount: 1,
    availableCount: 1,
    onLoanCount: 0,
  },
  {
    id: 'inv-chc-mold',
    locationId: 'community_health_centre',
    equipmentId: 'mold_moisture_assessment_tools',
    totalCount: 3,
    availableCount: 2,
    onLoanCount: 1,
  },
];
